import { jsx, jsxs } from "react/jsx-runtime";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { useScrollFade, useIsoLayoutEffect, revealInScroller } from "./scroller.js";
const NEXT_KEYS = /* @__PURE__ */ new Set(["ArrowRight", "ArrowDown"]);
const PREV_KEYS = /* @__PURE__ */ new Set(["ArrowLeft", "ArrowUp"]);
function SegmentedControlInner(props, forwardedRef) {
  const {
    options,
    value: valueProp,
    defaultValue,
    onValueChange,
    disabled = false,
    className,
    onKeyDown,
    onBlur,
    ...rest
  } = props;
  const isControlled = valueProp !== void 0;
  const [innerValue, setInnerValue] = React.useState(defaultValue ?? null);
  const selected = isControlled ? valueProp : innerValue;
  const groupRef = React.useRef(null);
  React.useImperativeHandle(forwardedRef, () => groupRef.current, []);
  const itemRefs = React.useRef(/* @__PURE__ */ new Map());
  useScrollFade(groupRef);
  const [focusedValue, setFocusedValue] = React.useState(null);
  const enabled = options.filter((option) => !disabled && !option.disabled).map((option) => option.value);
  let tabStop;
  if (focusedValue !== null && enabled.includes(focusedValue)) tabStop = focusedValue;
  else if (selected != null && enabled.includes(selected)) tabStop = selected;
  else tabStop = enabled[0];
  const mountedRef = React.useRef(false);
  useIsoLayoutEffect(() => {
    const group = groupRef.current;
    if (group && selected != null) revealInScroller(group, itemRefs.current.get(selected), mountedRef.current);
    mountedRef.current = true;
  }, [selected]);
  const select = (next) => {
    if (next === selected) return;
    if (!isControlled) setInnerValue(next);
    onValueChange?.(next);
  };
  const focusSegment = (next) => {
    const element = itemRefs.current.get(next);
    if (!element) return;
    element.focus({ preventScroll: true });
    if (groupRef.current) revealInScroller(groupRef.current, element, true);
  };
  const handleKeyDown = (event) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || enabled.length === 0) return;
    const rtl = getComputedStyle(event.currentTarget).direction === "rtl";
    const current = enabled.findIndex((v) => itemRefs.current.get(v) === event.target);
    let index;
    if (event.key === "Home") index = 0;
    else if (event.key === "End") index = enabled.length - 1;
    else if (NEXT_KEYS.has(event.key) || PREV_KEYS.has(event.key)) {
      const horizontal = event.key === "ArrowLeft" || event.key === "ArrowRight";
      let step = NEXT_KEYS.has(event.key) ? 1 : -1;
      if (rtl && horizontal) step = -step;
      const from = current === -1 ? step > 0 ? -1 : 0 : current;
      index = (from + step + enabled.length) % enabled.length;
    } else {
      return;
    }
    event.preventDefault();
    const target = enabled[index];
    if (target !== void 0) focusSegment(target);
  };
  const handleBlur = (event) => {
    onBlur?.(event);
    const next = event.relatedTarget;
    if (!next || !event.currentTarget.contains(next)) setFocusedValue(null);
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      ...rest,
      ref: groupRef,
      role: "group",
      className: cx("ev-segmented", "ev-scroll-fade", className),
      onKeyDown: handleKeyDown,
      onBlur: handleBlur,
      children: options.map((option) => {
        const isDisabled = disabled || Boolean(option.disabled);
        return /* @__PURE__ */ jsxs(
          "button",
          {
            ref: (element) => {
              if (element) itemRefs.current.set(option.value, element);
              else itemRefs.current.delete(option.value);
            },
            type: "button",
            "aria-pressed": option.value === selected,
            "aria-label": option.ariaLabel,
            disabled: isDisabled,
            tabIndex: option.value === tabStop ? 0 : -1,
            onFocus: () => setFocusedValue(option.value),
            onClick: () => select(option.value),
            children: [
              option.icon != null ? /* @__PURE__ */ jsx("span", { className: "ev-segmented__icon", "aria-hidden": "true", children: option.icon }) : null,
              option.label
            ]
          },
          option.value
        );
      })
    }
  );
}
const SegmentedControlBase = React.forwardRef(SegmentedControlInner);
SegmentedControlBase.displayName = "SegmentedControl";
const SegmentedControl = SegmentedControlBase;
export {
  SegmentedControl
};
//# sourceMappingURL=SegmentedControl.js.map
